import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import {
  FaShoppingCart,
  FaHeart,
  FaStar,
  FaTruck,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaRedoAlt,
} from "react-icons/fa";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../auth/AuthContext";
import { useVehicle } from "../contexts/VehicleContext";
import { productService, resolveProductMediaUrl } from "../services/apiService";
import api from "../utils/api";
import { calculateBulkPrice, DEFAULT_BULK_PRICING_TIERS } from "../utils/bulkPricing";
import { recommendationService } from "../services/recommendationService";
import InstallationGuide from "./InstallationGuide";
import ProductDetailLowerSections from "./ProductDetailLowerSections";
import ProductRecommendations from "./ProductRecommendations";
import { ProductDetailSkeleton } from "./Skeleton";

const PLACEHOLDER_IMAGE =
  "https://via.placeholder.com/600x400?text=Product+Image";

const ProductDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const { selectedVehicle } = useVehicle();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [inWishlist, setInWishlist] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [detailTab, setDetailTab] = useState("specifications");
  const [fitMake, setFitMake] = useState("");
  const [fitModel, setFitModel] = useState("");
  const [fitYear, setFitYear] = useState("");
  const [activeDeal, setActiveDeal] = useState(null);

  // Get product from location state (passed from product cards) or fetch it
  useEffect(() => {
    // If product data is passed via state, use it immediately (from View Details click)
    // This should be instant - no delay needed
    if (location.state?.product) {
      setLoading(true);
      const productData = location.state.product;

      // Track product view for recommendations
      recommendationService.trackProductView(productData);

      // Use the exact same image from the product card
      // If multiple images exist, use them; otherwise use the same image for all thumbnails
      const mainImageRaw = productData.image || (productData.images && productData.images[0]) || PLACEHOLDER_IMAGE;
      const mainImage =
        mainImageRaw === PLACEHOLDER_IMAGE ? mainImageRaw : resolveProductMediaUrl(mainImageRaw) || PLACEHOLDER_IMAGE;
      const productImages =
        productData.images && productData.images.length > 0
          ? productData.images.map((u) => resolveProductMediaUrl(u) || PLACEHOLDER_IMAGE)
          : [mainImage, mainImage, mainImage, mainImage]; // Use same image for all thumbnails

      // Calculate stock (random between 5-30)
      const stock = Math.floor(Math.random() * 25) + 5;

      // Calculate replacements price (60-80% of current price)
      const replacementsPrice = Math.floor(productData.price * (0.6 + Math.random() * 0.2));

      // Get category data from location state
      const categoryData = location.state?.category || { name: "Maintenance Service Parts", slug: "" };

      // Generate description based on product and category
      const description = categoryData.name === "Brake System"
        ? `${productData.name} for MARUTI SUZUKI, FORD, TATA, HYUNDAI and other vehicles - ${productData.partNumber} - ${productData.brand}`
        : `${productData.name} for CHEVROLET: TAVERA, MAHINDRA: GENIO, IMPERIO, QUANTO, SCORPIO, SCORPIO GETAWAY, XUV 500, XYLO, MARUTI: RITZ - ${productData.partNumber} - ${productData.brand}`;

      setProduct({
        ...productData,
        // Preserve original name and brand from productData
        name: productData.name || 'Unknown Product',
        brand: productData.brand || 'Unknown Brand',
        images: productImages,
        fullPartNumber: productData.partNumber || productData.fullPartNumber,
        mrp: productData.mrp || Math.round(productData.price * 1.05 * 100) / 100, // Add 5% if no MRP, rounded to 2 decimals
        stock: stock,
        isCompatible: false,
        compatibility: "Not compatible with your cars",
        compatibleVehicles: [],
        replacementsPrice: productData.replacementsPrice || replacementsPrice,
        category: categoryData.name || "Maintenance Service Parts",
        subCategory: productData.class || "Parts",
        subSubCategory: productData.name,
        seller: productData.soldBy || "Pune/SWA",
        fulfilledBySparelo: productData.fulfilledBySparelo || productData.fulfilledBysparelo || productData.fulfilledByBoodmo || false,
        spareloChoice: productData.spareloChoice || productData.boodmoChoice || false,
        deliveryDays: productData.deliveryDays || 4,
        returnDays: productData.returnDays || 10,
        description: description,
        freeDelivery: productData.freeDelivery || false,
        origin: productData.origin || "Aftermarket",
        class: productData.class || "Parts",
      });
      setLoading(false);
      return;
    }

    // Otherwise, fetch from API based on itemId
    if (itemId) {
      setLoading(true);
      const fetchProduct = async () => {
        try {
          // itemId is already the product ID (route: /catalog/part-p-:itemId)
          const productId = itemId;
          const result = await productService.getProductById(productId);
          const productData = result.product || result.data?.product || result;

          if (productData) {
            // Map backend product to frontend format
            const mainImage =
              productData.images && productData.images.length > 0
                ? resolveProductMediaUrl(productData.images[0]) || PLACEHOLDER_IMAGE
                : resolveProductMediaUrl(productData.imageUrl || '') || PLACEHOLDER_IMAGE;
            const productImages =
              productData.images && productData.images.length > 0
                ? productData.images.map((u) => resolveProductMediaUrl(u) || PLACEHOLDER_IMAGE)
                : [mainImage, mainImage, mainImage, mainImage];

            const categoryData = location.state?.category || { name: productData.category || "Maintenance Service Parts", slug: "" };

            const mappedProduct = {
              id: productData._id || productData.id || productId,
              name: productData.name || 'Unknown Product',
              brand: productData.brand || 'Unknown Brand',
              partNumber: productData.partNumber || productData.sku || 'N/A',
              fullPartNumber: productData.partNumber || productData.sku || 'N/A',
              images: productImages,
              price: productData.price || 0,
              mrp: productData.mrp || productData.price || 0,
              discount: productData.discount || 0,
              stock: productData.stock || 0,
              isCompatible: false,
              compatibility: productData.vehicleCompatibility && productData.vehicleCompatibility.length > 0
                ? productData.vehicleCompatibility.map(v =>
                  typeof v === 'object' && v !== null
                    ? [v.make, v.model, v.variant !== 'All' ? v.variant : '', v.yearFrom, v.yearTo ? `- ${v.yearTo}` : ''].filter(Boolean).join(' ')
                    : String(v)
                ).join(', ')
                : "Not compatible with your cars",
              compatibleVehicles: productData.vehicleCompatibility || [],
              replacementsPrice: productData.mrp ? Math.floor(productData.mrp * 0.7) : Math.floor(productData.price * 0.7),
              category: categoryData.name || productData.category || "Maintenance Service Parts",
              subCategory: productData.class || "Parts",
              subSubCategory: productData.name,
              seller: productData.soldBy || "Mumbai/MUM",
              vendorId: productData.vendorId,
              fulfilledBySparelo: true,
              spareloChoice: true,
              deliveryDays: productData.deliveryTime ? parseInt(productData.deliveryTime) || 4 : 4,
              returnDays: 10,
              description: productData.description || `${productData.name} - ${productData.partNumber} - ${productData.brand}`,
              freeDelivery: false,
              origin: productData.origin || "Aftermarket",
              class: productData.class || "Universal",
              isOEM: productData.origin === 'OEM',
              rating: 4.5,
              reviews: 0,
            };
            setProduct(mappedProduct);
            try {
              const dealIdParam = new URLSearchParams(location.search).get("dealId");
              const dealsRes = await api.get("/public/deals", {
                params: { productId: mappedProduct.id },
              });
              const deals = dealsRes.data?.data?.deals || [];
              const pick = dealIdParam
                ? deals.find((d) => String(d._id || d.id) === String(dealIdParam))
                : deals[0];
              if (pick) setActiveDeal(pick);
              else setActiveDeal(null);
            } catch {
              setActiveDeal(null);
            }
            // Track product view for recommendations
            recommendationService.trackProductView(mappedProduct);
          } else {
            // Product not found
            console.error('Product not found');
          }
        } catch (error) {
          console.error('Failed to fetch product:', error);
          // Fallback to mock data if API fails
          setProduct({
            id: itemId,
            name: "Product Not Found",
            partNumber: "N/A",
            fullPartNumber: "N/A",
            brand: "Unknown",
            seller: "N/A",
            price: 0,
            mrp: 0,
            category: "Maintenance Service Parts",
            subCategory: "Parts",
            subSubCategory: "Unknown",
            rating: 0,
            reviews: 0,
            images: [PLACEHOLDER_IMAGE],
            compatibility: "Product not available",
            isCompatible: false,
            stock: 0,
            isOEM: false,
            origin: "Aftermarket",
            class: "Unknown",
            fulfilledBySparelo: false,
            spareloChoice: false,
            deliveryDays: 0,
            replacementsPrice: 0,
            description: "Product not found or not available.",
            compatibleVehicles: [],
          });
        } finally {
          setLoading(false);
        }
      };

      fetchProduct();
    } else if (!location.state?.product) {
      // No itemId and no product data - show error
      setLoading(false);
    }
  }, [itemId, location.state?.product, location.state?.category]);

  useEffect(() => {
    if (!selectedVehicle) return;
    setFitMake(selectedVehicle.make || selectedVehicle.vehicleMake || "");
    setFitModel(selectedVehicle.model || selectedVehicle.vehicleModel || "");
    setFitYear(String(selectedVehicle.year || selectedVehicle.registrationYear || ""));
  }, [selectedVehicle]);

  const handleAddToCart = async () => {
    if (!isAuthenticated || !user) {
      // Store product and redirect to login
      if (product) {
        const cartProduct = {
          id: product.id,
          name: product.name || 'Unknown Product',
          brand: product.brand || 'Unknown Brand',
          price: product.mrp || product.price,
          discountPrice: product.mrp && product.mrp > product.price ? product.price : null,
          discount: product.mrp && product.mrp > product.price
            ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
            : null,
          imageUrl: product.images && product.images[0] ? product.images[0] : (product.image || PLACEHOLDER_IMAGE),
          rating: product.rating || 0,
          reviews: product.reviews || 0,
          partNumber: product.partNumber || product.fullPartNumber || '',
          seller: product.seller || 'Unknown Seller',
          vendorId: product.vendorId,
          quantity: quantity,
          bulkPricingTiers: product.bulkPricingTiers || DEFAULT_BULK_PRICING_TIERS,
        };
        localStorage.setItem('pendingCartProduct', JSON.stringify(cartProduct));
      }
      navigate('/login?redirect=cart');
      return;
    }

    if (product) {
      try {
        const dealId = activeDeal?._id || activeDeal?.id || location.state?.dealId || null;
        const unitPrice = activeDeal?.dealPrice != null
          ? Number(activeDeal.dealPrice)
          : (product.mrp && product.mrp > product.price ? product.price : product.mrp || product.price);
        const listPrice = activeDeal?.lockedBasePrice ?? product.mrp ?? product.price;
        const cartProduct = {
          id: product.id,
          dealId,
          name: product.name || 'Unknown Product',
          brand: product.brand || 'Unknown Brand',
          price: listPrice,
          discountPrice: unitPrice,
          discount: listPrice > unitPrice
            ? Math.round(((listPrice - unitPrice) / listPrice) * 100)
            : null,
          imageUrl: product.images && product.images[0] ? product.images[0] : (product.image || PLACEHOLDER_IMAGE),
          rating: product.rating || 0,
          reviews: product.reviews || 0,
          partNumber: product.partNumber || product.fullPartNumber || '',
          seller: product.seller || 'Unknown Seller',
          vendorId: product.vendorId,
          quantity: quantity,
          bulkPricingTiers: product.bulkPricingTiers || DEFAULT_BULK_PRICING_TIERS,
        };
        cartProduct.quantity = quantity;
        await addToCart(cartProduct, false);
        toast.success(`${quantity} x ${cartProduct.name} added to cart!`);
      } catch (error) {
        toast.error('Failed to add to cart. Please try again.');
      }
    } else {
      toast.warning('Product not loaded yet. Please wait...');
    }
  };

  const handleBuyNow = () => {
    if (!product) {
      toast.warning('Product not loaded yet. Please wait...');
      return;
    }

    const buyNowItem = {
      id: product.id,
      productId: product.id,
      name: product.name || 'Unknown Product',
      brand: product.brand || 'Unknown Brand',
      price: product.mrp || product.price,
      discountPrice: product.mrp && product.mrp > product.price ? product.price : null,
      imageUrl: product.images && product.images[0] ? product.images[0] : (product.image || PLACEHOLDER_IMAGE),
      partNumber: product.partNumber || product.fullPartNumber || '',
      seller: product.seller || 'Unknown Seller',
      vendorId: product.vendorId || null,
      quantity: quantity,
    };

    // Always clear any previous buy-now session
    localStorage.removeItem('buyNowItem');

    if (!isAuthenticated || !user) {
      // Save product for after login, then send to login
      localStorage.setItem('pendingBuyNowProduct', JSON.stringify(buyNowItem));
      navigate('/login', {
        state: { from: location.pathname, action: 'buyNow', returnTo: '/checkout/address' }
      });
      return;
    }

    // Authenticated: save buy-now item and go directly to checkout — do NOT touch cart
    localStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));
    navigate('/checkout/address');
  };

  const handleToggleWishlist = () => {
    setInWishlist(!inWishlist);
    toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist');
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0b1120] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-slate-400 mb-4">Product not found</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-blue-500"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  const rawPartNumber = product.partNumber || product.fullPartNumber || "—";
  const displayPartNumber =
    typeof rawPartNumber === "string" && rawPartNumber.length > 10
      ? `${rawPartNumber.substring(0, 4)}...${rawPartNumber.substring(rawPartNumber.length - 4)}`
      : rawPartNumber;

  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  const ratingVal = Number(product.rating) || 4.2;
  const reviewCount = product.reviews ?? product.reviewCount ?? 186;
  const soldCount = product.soldCount ?? 2340;
  const hsnCode = product.hsn || product.hsncode || "8708";
  const saveAmount =
    product.mrp && product.price && product.mrp > product.price
      ? Math.round(product.mrp - product.price)
      : 0;

  const galleryImages = product.images?.length
    ? product.images
    : [product.image || PLACEHOLDER_IMAGE];
  const thumbRow = [...galleryImages];
  while (thumbRow.length < 4) thumbRow.push(galleryImages[0]);

  const vehicleLine = selectedVehicle
    ? [
        selectedVehicle.make || selectedVehicle.vehicleMake,
        selectedVehicle.model || selectedVehicle.vehicleModel,
        selectedVehicle.variant || selectedVehicle.trim,
        selectedVehicle.year || selectedVehicle.registrationYear,
      ]
        .filter(Boolean)
        .join(" ")
    : null;

  const basePrice = product?.price || 0;
  const bulkPricing = calculateBulkPrice(basePrice, quantity, product?.bulkPricingTiers || DEFAULT_BULK_PRICING_TIERS);

  const specRows = [
    { label: "Brand", value: product.brand || "—" },
    { label: "Model / title", value: product.name || "—" },
    { label: "Part type", value: product.class || product.subCategory || "—" },
    { label: "Material", value: product.material || "Low-metallic ceramic compound" },
    { label: "SKU / Part no.", value: String(rawPartNumber) },
    { label: "OEM reference", value: product.oemNumber || rawPartNumber || "—" },
  ];

  return (
    <div className="min-h-screen bg-[#0b1120] text-slate-100">
      <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-8 py-6 sm:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Left — gallery + fitment */}
          <div className="lg:col-span-4 space-y-5">
            <div className="relative rounded-xl border border-slate-700/80 bg-slate-900/50 overflow-hidden aspect-square max-h-[420px] flex items-center justify-center">
              {product.isOEM && (
                <div className="absolute top-3 left-3 z-10 bg-blue-600 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-md">
                  OEM
                </div>
              )}
              <img
                loading="lazy"
                src={thumbRow[Math.min(selectedImage, thumbRow.length - 1)]}
                alt={product.name}
                className="w-full h-full object-contain p-4"
                onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {thumbRow.slice(0, 4).map((img, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedImage(index)}
                  className={`rounded-lg border-2 overflow-hidden aspect-square bg-slate-900/80 ${selectedImage === index ? "border-amber-500 ring-1 ring-amber-500/50" : "border-slate-700"}`}
                >
                  <img loading="lazy" src={img} alt="" className="w-full h-full object-contain p-1" onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }} />
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/70 p-4 space-y-3">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Fitment check</p>
              <p className="text-xs text-slate-400">Confirm this fits your vehicle:</p>
              <select
                value={fitMake}
                onChange={(e) => setFitMake(e.target.value)}
                className="w-full rounded-lg bg-slate-950 border border-slate-600 text-sm text-white px-3 py-2.5 outline-none focus:border-blue-500"
              >
                <option value="">Select Make</option>
                <option value="Maruti">Maruti</option>
                <option value="Hyundai">Hyundai</option>
                <option value="Tata">Tata</option>
                <option value="Mahindra">Mahindra</option>
              </select>
              <select
                value={fitModel}
                onChange={(e) => setFitModel(e.target.value)}
                className="w-full rounded-lg bg-slate-950 border border-slate-600 text-sm text-white px-3 py-2.5 outline-none focus:border-blue-500"
              >
                <option value="">Select Model</option>
                <option value="Swift">Swift</option>
                <option value="Dzire">Dzire</option>
                <option value="Baleno">Baleno</option>
              </select>
              <select
                value={fitYear}
                onChange={(e) => setFitYear(e.target.value)}
                className="w-full rounded-lg bg-slate-950 border border-slate-600 text-sm text-white px-3 py-2.5 outline-none focus:border-blue-500"
              >
                <option value="">Select Year</option>
                {["2024", "2023", "2022", "2021", "2020", "2019"].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => toast.success(fitMake && fitModel ? `Checking fit for ${fitMake} ${fitModel} ${fitYear}…` : "Select make, model, and year first.")}
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-3 transition-colors"
              >
                Check Fitment
              </button>
            </div>
          </div>

          {/* Right — detail */}
          <div className="lg:col-span-8 space-y-5 min-w-0">
            <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide">
              {(product.brand || "Brand").toUpperCase()}
              <span className="text-slate-600 mx-1.5">›</span>
              {(product.subCategory || product.category || "Parts").toUpperCase()}
            </p>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-white leading-tight">
              {product.name}
            </h1>
            <p className="text-[11px] sm:text-xs text-slate-500">
              SKU: <span className="text-slate-300 font-mono">{displayPartNumber}</span>
              <span className="text-slate-600 mx-2">·</span>
              OEM: <span className="text-slate-300 font-mono">{product.oemNumber || rawPartNumber}</span>
              <span className="text-slate-600 mx-2">·</span>
              HSN: <span className="text-slate-300 font-mono">{hsnCode}</span>
            </p>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm">
              <span className="flex items-center gap-0.5 text-amber-400">
                {[1, 2, 3, 4, 5].map((i) => (
                  <FaStar key={i} className={i <= Math.round(ratingVal) ? "text-amber-400" : "text-slate-600"} />
                ))}
              </span>
              <span className="text-amber-400 font-bold tabular-nums">{ratingVal.toFixed(1)}</span>
              <span className="text-amber-500/90 font-semibold">{reviewCount} verified reviews</span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-400">{soldCount.toLocaleString("en-IN")} sold</span>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-900/80 p-4 sm:p-5">
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-3xl sm:text-4xl font-black text-amber-400 tabular-nums">
                  ₹{bulkPricing.unitPrice.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
                {product.mrp && product.mrp > product.price && (
                  <>
                    <span className="text-sm text-slate-500 line-through tabular-nums">₹{product.mrp.toLocaleString("en-IN")}</span>
                    <span className="text-xs font-bold text-white bg-emerald-600 px-2 py-0.5 rounded">{discount}% off</span>
                  </>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                {saveAmount > 0 && (
                  <>You save ₹{saveAmount.toLocaleString("en-IN")} · </>
                )}
                Inc. GST (28%) · GST invoice available
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border border-emerald-600/50 bg-emerald-950/50 text-emerald-300">
                In Stock ({product.stock ?? 24} units)
              </span>
              <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-sky-900/80 text-sky-100 border border-sky-600/40">
                OEM Verified Fit
              </span>
              <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-amber-900/60 text-amber-100 border border-amber-600/40">
                1 Year Warranty
              </span>
              <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-slate-800 text-slate-200 border border-slate-600">
                Genuine {product.brand}
              </span>
            </div>

            <div className="rounded-xl border border-blue-500/40 bg-blue-950/25 px-4 py-3 flex gap-3">
              <FaCheckCircle className="text-blue-400 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-300 leading-relaxed">
                {vehicleLine ? (
                  <>Verified fitment for your <strong className="text-white">{vehicleLine}</strong>. This part is a direct OEM replacement where listed for your vehicle.</>
                ) : (
                  <>Select your vehicle in the fitment check to confirm this part matches your car.</>
                )}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch">
              <button
                type="button"
                onClick={handleBuyNow}
                className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-wide text-sm py-3.5 shadow-lg shadow-blue-900/30 transition-colors"
              >
                Buy Now
              </button>
              <button
                type="button"
                onClick={handleAddToCart}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-black uppercase tracking-wide text-sm py-3.5 shadow-lg shadow-amber-900/20 transition-colors"
              >
                <FaShoppingCart />
                Add to Cart
              </button>
              <button
                type="button"
                onClick={handleToggleWishlist}
                className="sm:w-14 h-14 shrink-0 rounded-xl border border-slate-600 bg-slate-900 flex items-center justify-center text-slate-300 hover:text-rose-400 hover:border-slate-500 transition-colors"
                aria-label="Wishlist"
              >
                <FaHeart className={inWishlist ? "text-rose-400 fill-current" : ""} />
              </button>
            </div>

            <div className="space-y-3 text-sm text-slate-400 border-t border-slate-800 pt-5">
              <div className="flex items-start gap-3">
                <FaTruck className="text-amber-600/90 mt-0.5 shrink-0" />
                <span>Delivery by <strong className="text-slate-200">Tomorrow, 8:00 PM</strong></span>
              </div>
              <div className="flex items-start gap-3">
                <FaRedoAlt className="text-amber-600/90 mt-0.5 shrink-0" />
                <span>{product.returnDays ?? 30}-day returns · easy process</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <FaMapMarkerAlt className="text-amber-600/90 shrink-0" />
                <span>
                  Sold by <strong className="text-slate-200">{product.seller || product.soldBy || "AutoZone Delhi"}</strong>
                </span>
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-950/80 text-emerald-300 border border-emerald-700/50">Verified Seller</span>
              </div>
              <Link to="/service/booking" className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-semibold">
                Need installation? Book a garage service →
              </Link>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-12 border-t border-slate-800 pt-8">
          <div className="flex flex-wrap gap-1 border-b border-slate-800">
            {[
              { id: "specifications", label: "Specifications" },
              { id: "description", label: "Description" },
              { id: "faq", label: "FAQ (12)" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setDetailTab(t.id)}
                className={`px-4 py-3 text-xs sm:text-sm font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${detailTab === t.id ? "border-amber-500 text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="py-8 min-h-[12rem]">
            {detailTab === "specifications" && (
              <div className="max-w-2xl space-y-0">
                {specRows.map((row) => (
                  <div key={row.label} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-8 gap-y-1 py-3 border-b border-slate-800/80 last:border-0">
                    <span className="text-sm text-slate-500">{row.label}</span>
                    <span className="text-sm text-white font-medium">{row.value}</span>
                  </div>
                ))}
                {(product?.bulkPricingTiers || DEFAULT_BULK_PRICING_TIERS).length > 1 && (
                  <div className="mt-6 rounded-lg border border-slate-700 bg-slate-900/50 p-4">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Bulk pricing</p>
                    <div className="space-y-1 text-xs text-slate-300">
                      {(product?.bulkPricingTiers || DEFAULT_BULK_PRICING_TIERS).slice(1).map((tier, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span>{tier.minQty}+ units</span>
                          <span className="text-emerald-400 font-semibold">{tier.discount}% off</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {detailTab === "description" && (
              <div className="space-y-8 text-slate-300 text-sm leading-relaxed max-w-3xl">
                <p>{product.description}</p>
                <InstallationGuide product={product} />
              </div>
            )}
            {detailTab === "faq" && (
              <div className="max-w-3xl space-y-4 text-sm text-slate-300">
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                  <p className="font-bold text-white mb-1">Is this a genuine part?</p>
                  <p className="text-slate-400">Yes — listings marked OEM or Genuine are sourced per channel policy.</p>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                  <p className="font-bold text-white mb-1">What is the return window?</p>
                  <p className="text-slate-400">Standard returns apply for unopened / fitment issues within the policy period.</p>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                  <p className="font-bold text-white mb-1">Do you provide installation?</p>
                  <p className="text-slate-400">You can book garage service from your account or the link above.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <ProductDetailLowerSections
          product={product}
          summaryRating={ratingVal}
          reviewCount={reviewCount}
        />

        <div className="mt-10 border-t border-slate-800 pt-10">
          <ProductRecommendations
            title="You may also like"
            currentProduct={product}
            type="similar"
            limit={8}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
